// usePlaybackManager.js
import { ref } from "vue";
import usePlaybackEngine from "./usePlaybackEngine";
import { featureCollection } from "@turf/helpers";
import C from "../Constants";

export function usePlaybackManager(allObjects, startUnix, mapbox) {
  const isPlaying = ref(false);
  const currentTime = ref(startUnix);
  const engines = new Map();
  const latestFeatureMap = new Map(); // objectId → feature
  let speedFactor = 1.0;
  let tickCallback = null;
  let rafId = null;
  let lastFrameTime = performance.now();

  // 初期化
  for (const obj of allObjects) {
    engines.set(
      obj.objectId,
      usePlaybackEngine(
        obj.objectId,
        obj.points,
        (objectId, feature) => {
          if (feature) {
            latestFeatureMap.set(objectId, feature);
          } else {
            latestFeatureMap.delete(objectId);
          }
        },
        obj.source,
        obj.properties
      )
    );
  }

  const update = now => {
    const delta = (now - lastFrameTime) / 1000;
    lastFrameTime = now;
    currentTime.value += delta * speedFactor;

    engines.forEach(engine => engine.update(currentTime.value));

    const visibleFeatures = [];
    const nowSec = currentTime.value;

    for (const [objectId, feature] of latestFeatureMap.entries()) {
      if (!feature || !feature.geometry) continue;

      const featureTime = feature.properties.t;
      if (nowSec - featureTime <= 60) {
        visibleFeatures.push(feature);
      }
    }

    mapbox.setSourceData(
      C.SOURCE_ID.RG_HISTORY_SOURCE,
      featureCollection(visibleFeatures)
    );

    if (tickCallback) {
      tickCallback(nowSec);
    }

    rafId = requestAnimationFrame(update);
  };

  const play = () => {
    if (!isPlaying.value) {
      isPlaying.value = true;
      lastFrameTime = performance.now();
      rafId = requestAnimationFrame(update);
    }
  };

  const pause = () => {
    isPlaying.value = false;
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };

  const seek = time => {
    const wasPlaying = isPlaying.value;

    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }

    currentTime.value = time;

    if (tickCallback) {
      tickCallback(currentTime.value);
    }

    // latestFeatureMap を更新する
    latestFeatureMap.clear();

    engines.forEach((engine, objectId) => {
      engine.seek(time);
      const f = engine.getLatestFeature();
      if (f && f.properties && f.geometry && f.properties.t >= time - 60) {
        latestFeatureMap.set(f.properties.objectId, f);
      } else {
        latestFeatureMap.delete(objectId);
      }
    });

    // GeoJSON を Mapbox に反映
    mapbox.setSourceData(
      C.SOURCE_ID.RG_HISTORY_SOURCE,
      featureCollection(Array.from(latestFeatureMap.values()))
    );

    if (wasPlaying) {
      isPlaying.value = true;
      lastFrameTime = performance.now();
      rafId = requestAnimationFrame(update);
    }

    return wasPlaying;
  };

  const stopAnimationOnly = () => {
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };

  const onTick = cb => {
    tickCallback = cb;
  };

  const setSpeed = factor => {
    speedFactor = factor;
  };

  const getLatestFeature = objectId => latestFeatureMap.get(objectId);

  return {
    play,
    pause,
    seek,
    stopAnimationOnly,
    isPlaying,
    onTick,
    setSpeed,
    getLatestFeature,
  };
}
